import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface DatabaseStats {
  status: string;
  message: string;
  database: string;
  collections: string[];
  stats: {
    collections: number;
    indexes: number;
    objects: number;
    avgObjSize: number;
    dataSize: number;
    storageSize: number;
  };
}

const DatabaseTest = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DatabaseStats | null>(null);

  useEffect(() => {
    const checkDatabase = async () => {
      try {
        const data = await api<DatabaseStats>('/api/db-test');
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to connect to database');
      } finally {
        setLoading(false);
      }
    };

    checkDatabase();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-8">
      <Card>
        <CardHeader>
          <CardTitle>Database Connection Test</CardTitle>
          <CardDescription>Checking connection to MongoDB</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-red-500">
              <h3 className="font-bold">Error:</h3>
              <p>{error}</p>
            </div>
          ) : stats ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full ${stats.status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="font-medium">{stats.message}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-bold">Database:</h3>
                  <p>{stats.database}</p>
                </div>
                <div>
                  <h3 className="font-bold">Collections:</h3>
                  <p>{stats.collections.join(', ') || 'No collections'}</p>
                </div>
              </div>

              <div>
                <h3 className="font-bold">Statistics:</h3>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>Collections: {stats.stats.collections}</div>
                  <div>Indexes: {stats.stats.indexes}</div>
                  <div>Objects: {stats.stats.objects}</div>
                  <div>Avg Object Size: {Math.round(stats.stats.avgObjSize / 1024)}KB</div>
                  <div>Data Size: {Math.round(stats.stats.dataSize / 1024 / 1024)}MB</div>
                  <div>Storage Size: {Math.round(stats.stats.storageSize / 1024 / 1024)}MB</div>
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseTest; 